import { Box, TextField, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  Marker,
  TileLayer,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import type { FieldSummary } from '@/api/fields';
import { FieldMarkerCluster } from '@/components/Map/FieldMarkerCluster';
import {
  createLabeledFieldMapIcon,
  createScenarioLocationMapIcon,
  defaultFieldMapIcon,
} from '@/components/Map/fieldMapIcon';
import { MapBaseLayers } from '@/components/Map/mapLayers';
import {
  FIELD_MARKER_ZOOM,
  MENA_DEFAULT_ZOOM,
  MENA_MAP_CENTER,
} from '@/constants/fieldMap';
import type { LatLng } from '@/utils/fieldCoordinate';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';

const labeledMarkerSx = {
  '& .field-map-labeled-marker': {
    background: 'transparent',
    border: 'none',
  },
  '& .field-map-labeled-marker__wrap': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 140,
    height: 79,
    pointerEvents: 'auto',
  },
  '& .field-map-labeled-marker__pin': {
    width: 25,
    height: 41,
    display: 'block',
    flexShrink: 0,
  },
  '& .field-map-labeled-marker__label': {
    marginTop: '2px',
    maxWidth: 136,
    maxHeight: 36,
    overflow: 'hidden',
    padding: '2px 6px',
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.92)',
    border: '1px solid rgba(0,0,0,0.18)',
    color: '#1a1a1a',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'center',
    wordBreak: 'break-word',
    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
    userSelect: 'none',
  },
  '& .field-map-labeled-marker--scenario .field-map-labeled-marker__label': {
    backgroundColor: '#d32f2f',
    color: '#fff',
    borderColor: '#8e0000',
  },
} as const;

interface FieldCoordinatePickerProps {
  enabled?: boolean;
  position: LatLng | null;
  onChange: (position: LatLng | null) => void;
  readOnly?: boolean;
  mapHeight?: number;
  showHint?: boolean;
  showCoordinates?: boolean;
  showControls?: boolean;
  /**
   * `field` (default): blue field pin — history / field forms / global map helpers.
   * `scenario`: red scenario pin — only for scenario location editors.
   */
  markerKind?: 'field' | 'scenario';
  /** Label for the editable pin when markerKind is field (optional). */
  pinLabel?: string;
  /** Visible catalogue fields to plot as labeled pins (scenario editors only). */
  fields?: FieldSummary[];
}

function MapClickHandler({
  onChange,
}: {
  onChange: (position: LatLng) => void;
}) {
  useMapEvents({
    click(event) {
      onChange({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
}

export const FieldCoordinatePicker: React.FC<FieldCoordinatePickerProps> = ({
  enabled = true,
  position,
  onChange,
  readOnly = false,
  mapHeight = 280,
  showHint = true,
  showCoordinates = true,
  showControls = true,
  markerKind = 'field',
  pinLabel,
  fields = [],
}) => {
  const [mapReady, setMapReady] = useState(false);
  // Freeze initial view so placing/moving the pin never recenters or rezooms the map.
  const initialViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  if (initialViewRef.current === null) {
    initialViewRef.current = {
      center: position
        ? [position.lat, position.lng]
        : [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng],
      zoom: position ? FIELD_MARKER_ZOOM : MENA_DEFAULT_ZOOM,
    };
  }

  useEffect(() => {
    if (!enabled) {
      setMapReady(false);
      return;
    }
    const timer = window.setTimeout(() => setMapReady(true), 50);
    return () => {
      window.clearTimeout(timer);
      setMapReady(false);
    };
  }, [enabled]);

  // Catalogue overlay is only for scenario editors — never paint scenario pins into field maps.
  const catalogueFields = markerKind === 'scenario' ? fields : [];

  const fieldPoints = useMemo(
    () =>
      catalogueFields
        .map((field) => {
          const pt = parsePointCoordinate(field.coordinate);
          if (!pt) return null;
          return { id: field.id, position: pt, data: field };
        })
        .filter((p): p is { id: string; position: LatLng; data: FieldSummary } => p != null),
    [catalogueFields],
  );

  const positionIcon = useMemo(() => {
    if (markerKind === 'scenario') {
      return createScenarioLocationMapIcon(pinLabel?.trim() || 'Scenario');
    }
    const label = pinLabel?.trim();
    if (label) {
      return createLabeledFieldMapIcon(label, false);
    }
    return defaultFieldMapIcon;
  }, [markerKind, pinLabel]);

  const handleLatChange = (value: string) => {
    const lat = value === '' ? null : Number(value);
    if (lat === null || Number.isNaN(lat)) {
      onChange(null);
      return;
    }
    onChange({ lat, lng: position?.lng ?? MENA_MAP_CENTER.lng });
  };

  const handleLngChange = (value: string) => {
    const lng = value === '' ? null : Number(value);
    if (lng === null || Number.isNaN(lng)) {
      onChange(null);
      return;
    }
    onChange({ lat: position?.lat ?? MENA_MAP_CENTER.lat, lng });
  };

  return (
    <Box>
      {showHint && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {readOnly
            ? 'Field location on the map'
            : markerKind === 'scenario'
              ? 'Coordinates (optional) — enter manually, zoom the map, switch layers, or click to place a pin. Catalogue fields are shown for reference.'
              : 'Coordinates (optional) — enter manually, zoom the map, switch layers, or click to place a pin'}
        </Typography>
      )}
      {showCoordinates && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <TextField
            label="Latitude"
            type="number"
            size="small"
            fullWidth
            value={position?.lat ?? ''}
            onChange={(e) => handleLatChange(e.target.value)}
            inputProps={{ step: 'any', readOnly }}
            disabled={readOnly}
          />
          <TextField
            label="Longitude"
            type="number"
            size="small"
            fullWidth
            value={position?.lng ?? ''}
            onChange={(e) => handleLngChange(e.target.value)}
            inputProps={{ step: 'any', readOnly }}
            disabled={readOnly}
          />
        </Box>
      )}
      <Box
        sx={{
          height: mapHeight,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          '& .leaflet-container': { height: '100%', width: '100%', zIndex: 0 },
          '& .leaflet-control-layers': { fontSize: '12px' },
          ...labeledMarkerSx,
          ...(!showControls && {
            '& .leaflet-control-container': { display: 'none' },
          }),
        }}
      >
        {mapReady ? (
          <MapContainer
            key={enabled ? 'field-map-active' : 'field-map-inactive'}
            center={initialViewRef.current.center}
            zoom={initialViewRef.current.zoom}
            minZoom={2}
            maxZoom={14}
            scrollWheelZoom={showControls}
            dragging={showControls}
            doubleClickZoom={showControls}
            zoomControl={false}
            attributionControl={showControls}
            style={{ height: '100%', width: '100%' }}
          >
            {showControls ? (
              <MapBaseLayers />
            ) : (
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            )}
            {showControls && <ZoomControl position="bottomright" />}
            {!readOnly && <MapClickHandler onChange={onChange} />}
            {fieldPoints.length > 0 && (
              <FieldMarkerCluster
                points={fieldPoints}
                onSelect={() => undefined}
                showLabels
                getLabel={(p) => p.data.name}
              />
            )}
            {position && (
              <Marker
                position={[position.lat, position.lng]}
                icon={positionIcon}
                zIndexOffset={markerKind === 'scenario' ? 20000 : 1000}
              />
            )}
          </MapContainer>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
